/**
 * ScopeAssessmentCard — premium "SCOPEvision Property Assessment" summary card.
 *
 * Renders on the indicative result and cost-breakdown pages once a draft has
 * been generated. Calls /api/scope/drafts/:id/assessment which uses our
 * SCOPEvision engine to produce a 3-paragraph property assessment (Property
 * Summary / Scope Notes / Market Context).
 *
 * Behaviour:
 *  - Shimmer skeleton while SCOPEvision composes the summary (~3-5s)
 *  - Falls back to a deterministic boilerplate if generation fails
 *  - "✨ SCOPEvision" pill + "FREE for a limited time" launch ribbon
 *
 * No client-facing references to underlying AI provider — all consumer copy
 * uses the SCOPEvision brand name (May-2026 user spec).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ScopeAssessmentCard = ({ draftId, payload, autoLoad = true }) => {
  const [loading, setLoading] = useState(false);
  const [html, setHtml] = useState('');
  const [fallback, setFallback] = useState(false);
  const [gated, setGated] = useState(null);
  const [error, setError] = useState('');

  const generate = async () => {
    if (loading) return;
    if (!draftId && !payload) return;
    setLoading(true);
    setError('');
    try {
      const r = draftId
        ? await axios.post(`${API}/scope/drafts/${draftId}/assessment`)
        : await axios.post(`${API}/assessment/quick`, payload);
      if (r.data?.assessment_html) {
        setHtml(r.data.assessment_html);
        setFallback(!!r.data.fallback);
      } else {
        setError('No assessment returned.');
      }
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data || {};
      if (status === 402 && data?.gated) {
        setGated({
          message: data.message || 'SCOPE Assessment is available on Pro and Pro+ subscriptions.',
        });
      } else {
        setError(data?.error || data?.message || 'Could not load assessment.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoLoad && (draftId || payload)) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, JSON.stringify(payload || {})]);

  if (!draftId && !payload) return null;

  return (
    <div
      data-testid="scope-assessment-card"
      style={{
        background: 'linear-gradient(135deg, #0F2742 0%, #1E40AF 60%, #2563EB 100%)',
        borderRadius: 16,
        padding: '24px 28px 22px',
        color: '#fff',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 12px 40px -12px rgba(37,99,235,.5), 0 1px 0 rgba(255,255,255,.08) inset',
      }}
    >
      {/* Decorative glow orbs */}
      <div aria-hidden style={{
        position: 'absolute', top: -40, right: -40, width: 180, height: 180,
        background: 'radial-gradient(circle, rgba(255,255,255,.15) 0%, transparent 60%)',
        pointerEvents: 'none',
      }} />
      <div aria-hidden style={{
        position: 'absolute', bottom: -60, left: -50, width: 220, height: 220,
        background: 'radial-gradient(circle, rgba(167,139,250,.18) 0%, transparent 60%)',
        pointerEvents: 'none',
      }} />

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, position: 'relative' }}>
        <div style={{
          width: 32, height: 32, borderRadius: 9,
          background: 'rgba(255,255,255,.18)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Sparkles className="w-4 h-4" style={{ color: '#fff' }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, letterSpacing: '.14em', fontWeight: 800, opacity: 0.78, textTransform: 'uppercase' }}>
            SCOPE Assessment
          </div>
          <div style={{ fontSize: 16, fontWeight: 800, marginTop: 1 }}>
            SCOPEvision property summary
          </div>
        </div>
        <span data-testid="scope-assessment-launch-badge"
          style={{
            background: 'linear-gradient(135deg,#10B981 0%,#059669 100%)',
            padding: '4px 11px', borderRadius: 999,
            fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em',
            boxShadow: '0 2px 6px rgba(5,150,105,.4)',
          }}>
          FREE — LAUNCH OFFER
        </span>
      </div>

      {/* Body */}
      <div style={{ position: 'relative' }}>
        {gated ? (
          <div data-testid="scope-assessment-gated" style={{ fontSize: 13.5, lineHeight: 1.65, color: 'rgba(255,255,255,.92)' }}>
            <p style={{ margin: '0 0 10px' }}>{gated.message}</p>
            <a href="/pricing" style={{ display: 'inline-block', background: '#fff', color: '#1E40AF', textDecoration: 'none', fontWeight: 700, padding: '8px 16px', borderRadius: 8, fontSize: 13 }}>
              See Pro plans →
            </a>
          </div>
        ) : loading ? (
          <div data-testid="scope-assessment-loading" style={{ display: 'flex', alignItems: 'center', gap: 10, opacity: 0.85 }}>
            <Loader2 className="w-4 h-4" style={{ color: '#fff', animation: 'spin 1s linear infinite' }} />
            <span style={{ fontSize: 13 }}>Analysing your project…</span>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          </div>
        ) : error ? (
          <div data-testid="scope-assessment-error" style={{ fontSize: 13, color: 'rgba(255,255,255,.85)' }}>
            {error}{' '}
            <button onClick={generate} style={{ background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,.4)', padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', marginLeft: 6 }}>
              Try again
            </button>
          </div>
        ) : html ? (
          <div
            data-testid="scope-assessment-body"
            style={{
              fontSize: 13.5, lineHeight: 1.65, color: 'rgba(255,255,255,.95)',
            }}
            // We control the HTML server-side from a tightly-constrained
            // SCOPEvision prompt and a small markdown→html converter; no
            // untrusted user input.
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : null}
      </div>

      {/* Footer */}
      {!loading && html && !gated && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,.18)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,.65)', letterSpacing: '.04em' }}>
            {fallback
              ? 'Standard summary — full AI version unavailable for this draft.'
              : 'Generated by SCOPEvision · refreshed when you change scope'}
          </span>
          <button
            onClick={generate}
            data-testid="scope-assessment-regen"
            style={{
              background: 'rgba(255,255,255,.14)', color: '#fff',
              border: '1px solid rgba(255,255,255,.22)',
              padding: '5px 14px', borderRadius: 18,
              fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}
          >
            ↻ Regenerate
          </button>
        </div>
      )}

      {/* Style — paragraph spacing inside dangerouslySetInnerHTML */}
      <style>{`
        [data-testid="scope-assessment-body"] p { margin: 0 0 12px; }
        [data-testid="scope-assessment-body"] p:last-child { margin: 0; }
        [data-testid="scope-assessment-body"] strong { color: #DBEAFE; font-weight: 700; }
      `}</style>
    </div>
  );
};

export default ScopeAssessmentCard;
